/**
 * @module builder
 */
import type { OptionsDocument, QuoteArea, QuoteDocument } from 'paintscout'

import findIndex from 'lodash/findIndex'
import cloneDeep from 'lodash/cloneDeep'
import { calculateQuote, moveTo } from '../../util'
import { uuid } from '../../../util'
import { getItem } from '../../items/get-items/get-item/get-item'
import { getFileKeysForItem } from '../../files/get-file-keys-for-item/get-file-keys-for-item'
import { getFiles } from '../../files/get-files/get-files'
import { updateFiles } from '../../files/update-files/update-files'
import getSectionSubstrates from '../get-section-substrates'
import type { QuoteItemSection } from '../..'

export function copyArea(args: {
  quote: QuoteDocument
  options: OptionsDocument
  key: string
  calculate?: boolean
  useKey?: string
  section?: QuoteItemSection
}): QuoteDocument {
  const { quote, options, key, calculate = true, useKey, section } = args
  const newKey = useKey ? useKey : uuid()
  const area = cloneDeep(quote.areas[key])
  area.key = newKey

  delete area.images

  const orderItem = {
    key: newKey,
    type: 'area'
  }

  // Clear areaSubstrates not in the copied section, if none given keep all(grouped areas)
  // update keys for substrates so unique from the area we are copying from
  const cleanArea = cleanAreaKeys(moveTo(getSectionSubstrates(area, section), section))
  const areaOrder = [...quote.order.area]
  const index = findIndex(areaOrder, { key })
  areaOrder.splice(index + 1, 0, orderItem)

  const updateableItem = getItem({ quote, options, key })
  const fileKeys = getFileKeysForItem({ item: updateableItem })
  const copiedFiles = getFiles({ quote, keys: fileKeys }).map((file) => ({
    ...file,
    key: uuid()
  }))

  cleanArea.files = copiedFiles.map((file) => file.key)

  const updatedQuote = {
    ...quote,
    areas: {
      ...quote.areas,
      [newKey]: cleanArea
    },
    order: {
      ...quote.order,
      area: areaOrder
    }
  }

  const withFiles = updateFiles({ quote: updatedQuote, files: copiedFiles })

  return calculate ? calculateQuote({ quote: withFiles, options }) : withFiles
}

/**
 * Update keys for each substrateOrderItem and its corrosponding substrate so they are unique
 * from the area we are copying from
 */
function cleanAreaKeys(area: QuoteArea) {
  const newArea = area.substrateOrder.reduce((areaAcc, oldOrderItem) => {
    const oldSubstrateKey = oldOrderItem.key
    // find index of acc.substrateOrder with key oldSubstrateKey
    const orderIndex = findIndex(areaAcc.substrateOrder, { key: oldSubstrateKey })
    // find the substrate section key, and the index at which the rate is in the section
    const { sectionKey, rateIndex } = Object.keys(areaAcc.substrates).reduce(
      (acc, substrateKey) => {
        if (acc.sectionKey) return acc
        const rateIndex = findIndex(areaAcc.substrates[substrateKey], { key: oldSubstrateKey })
        if (rateIndex > -1) return { sectionKey: substrateKey, rateIndex }
        return acc
      },
      { sectionKey: '', rateIndex: -1 }
    )

    // Update the keys
    const newKey = uuid()
    areaAcc.substrateOrder[orderIndex] = {
      ...areaAcc.substrateOrder[orderIndex],
      key: newKey
    }
    areaAcc.substrates[sectionKey][rateIndex] = {
      ...areaAcc.substrates[sectionKey][rateIndex],
      key: newKey
    }

    return areaAcc
  }, area)

  return newArea
}
